import { FC, Fragment } from 'react';
import { useAppDispatch, useAppSelector } from '@core/hooks/redux-hooks';
import { selectSortedCasesStructure } from '@core/store/project-slice-selectors';
import classNames from 'classnames';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { monthDayCellViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { InstrumentType } from '@app/shared/models/contracts/enums/shared-enums';
import { cloneToMutableData, enumKeyByValue, sumBy } from '@app/shared/helpers';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import { percentageFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import {
  getDlomIndexByInstrumentId,
  getDlomIndexByTrancheId,
} from '@app/shared/helpers/equity-instruments/get-equity-instrument-index';
import * as projectActions from '@core/store/project-slice';
import { CaseDto } from '@app/shared/models/contracts/project-dto';
import {
  TABLE_COL_WIDTH_ALTERNATIVE,
  TABLE_LABELS_COL_WIDTH_ALTERNATIVE,
} from '@app/shared/constants/table-config';
import { selectInstruments } from '@app/core/store/capital-structure-slice-selectors';
import { useLocale } from '@app/core/hooks/useLocale';
import useTriggerEffect from '@app/core/hooks/useTriggerEffect';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import { minMaxValidator } from '@app/core/validations/hook-forms/validators';

const DLOMTable: FC = () => {
  const { l } = useLocale();
  const projectDraft = useAppSelector((state) => state.project.projectDraft);
  const casesStructure = useAppSelector(selectSortedCasesStructure);

  const dispatch = useAppDispatch();
  const instruments = useAppSelector(selectInstruments);
  const valuedInstruments = instruments.filter((instrument) => instrument.shouldBeValued);
  const sortedValuedInstrument = useInstrumentSort(valuedInstruments);
  const columnsCount =
    sumBy(projectDraft.pwermInput.cases, (caseItem) => caseItem.forecasts.length) + 1;

  const formMethods = useForm({
    ...formConfigBase,
    defaultValues: {
      cases: projectDraft.pwermInput.cases,
    },
  });

  const { trigger, getValues } = formMethods;

  useTriggerEffect(trigger);

  const formSubmitHandler = () => {
    const {
      pwermInput: { cases },
    } = projectDraft;
    const casesClone = cloneToMutableData(cases);
    const formValues = getValues();
    const formCases = cloneToMutableData(formValues.cases);

    formCases.forEach((caseItem, caseIndex) => {
      caseItem.forecasts.forEach((forecast, forecastIndex) => {
        forecast.dlom.forEach((dlomItem) => {
          const existingDlomIndex = casesClone[caseIndex].forecasts[forecastIndex].dlom.findIndex(
            (d) => d.instrumentId === dlomItem.instrumentId && d.trancheId === dlomItem.trancheId
          );
          if (existingDlomIndex !== -1) {
            casesClone[caseIndex].forecasts[forecastIndex].dlom[existingDlomIndex] = dlomItem;
          } else {
            casesClone[caseIndex].forecasts[forecastIndex].dlom.push(dlomItem);
          }
        });
      });
    });

    const updatedProjectDraft = {
      ...projectDraft,
      pwermInput: {
        ...projectDraft.pwermInput,
        cases: casesClone,
      },
    };

    dispatch(projectActions.updateProjectDraft({ project: updatedProjectDraft }));
  };

  const findSortedCaseIndex = (caseId: string) => {
    return projectDraft.pwermInput.cases.findIndex((item) => item.caseId === caseId);
  };

  return (
    <FormProvider {...formMethods}>
      <form
        data-cy="dlom-form"
        onBlur={formMethods.handleSubmit(
          formSubmitHandler as SubmitHandler<{ cases: CaseDto[] }>,
          formSubmitHandler as SubmitErrorHandler<{ cases: CaseDto[] }>
        )}>
        <table
          className="table-primary table-primary--zebra table-primary--fixed table-primary"
          style={{
            width: columnsCount
              ? columnsCount * TABLE_COL_WIDTH_ALTERNATIVE +
                TABLE_LABELS_COL_WIDTH_ALTERNATIVE +
                'px'
              : undefined,
          }}>
          <colgroup>
            <col style={{ width: `${TABLE_LABELS_COL_WIDTH_ALTERNATIVE}px` }} />
            {[...Array(columnsCount - 1)].map((_, index) => (
              <col key={`col-${index}`} style={{ width: `${TABLE_COL_WIDTH_ALTERNATIVE}px` }} />
            ))}
          </colgroup>
          <thead className="table-primary__sticky-section table-primary__sticky-section table-primary__sticky-section--table-single-row-header">
            <tr>
              <th className="table-primary__cell--header-tertiary table-primary__cell--vertical-separator" />
              {casesStructure.map((caseItem, index) => (
                <Fragment key={caseItem.caseId + '-forecast-item-' + index}>
                  <th
                    colSpan={caseItem.forecasts.length}
                    className="table-primary__cell--header-tertiary table-primary__cell--center table-primary__cell--vertical-separator table-primary__cell--group-separator-vertical">
                    <span>{caseItem.narrative}</span>
                  </th>
                </Fragment>
              ))}
            </tr>

            <tr>
              <th className="table-primary__cell--vertical-separator">{l('_ExitDate')}</th>
              {casesStructure.map((caseItem) => {
                return caseItem.forecasts.map((forecast, index) => (
                  <Fragment key={forecast.forecastId + 'forecast-year-value'}>
                    <td
                      className={classNames({
                        'table-primary__cell--vertical-separator':
                          index === caseItem.forecasts.length - 1,
                      })}>
                      <CellValue value={forecast.forecastYear} {...monthDayCellViewRules} />
                    </td>
                  </Fragment>
                ));
              })}
            </tr>
            <tr>
              <th className="table-primary__cell--header" colSpan={columnsCount}>
                {l('_DLOM')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedValuedInstrument.map((instrument, instrumentIndex) => {
              return (
                <Fragment key={`instrument-row-fragment-${instrument.instrumentId}`}>
                  <tr
                    key={'instrument-row-wrapper' + instrument.instrumentId}
                    data-testid={`dlom-instrument-row-${instrument.instrumentId}`}>
                    <td className="table-primary__cell--vertical-separator">
                      {instrument.instrumentNarrative}
                    </td>
                    {instrument.type !==
                      enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity) && (
                      <Fragment key={`ordinary-equity-row-${instrument.instrumentId}`}>
                        {casesStructure.map((caseItem) => {
                          return caseItem.forecasts.map((forecast, forecastIndex) => (
                            <td
                              className={classNames({
                                'table-primary__cell--vertical-separator':
                                  caseItem.forecasts.length - 1 === forecastIndex,
                              })}
                              key={forecast.forecastId + 'value'}>
                              <GridTextField
                                name={`cases[${findSortedCaseIndex(
                                  caseItem.caseId
                                )}].forecasts[${forecastIndex}].dlom[${getDlomIndexByInstrumentId(
                                  forecast,
                                  instrument.instrumentId
                                )}].value`}
                                {...percentageFieldFormattingProps}
                                rules={{ ...minMaxValidator(0, 100) }}
                              />
                            </td>
                          ));
                        })}
                      </Fragment>
                    )}
                    {instrument.type ===
                      enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity) &&
                      casesStructure.map((caseItem) => {
                        return caseItem.forecasts.map((forecast, forecastIndex) => (
                          <td
                            className={classNames({
                              'table-primary__cell--vertical-separator':
                                caseItem.forecasts.length - 1 === forecastIndex,
                            })}
                            key={forecast.forecastId + 'ordinary-equity-value'}>
                            {Boolean(!instrument.payoutLogic?.length) && (
                              <GridTextField
                                name={`cases[${findSortedCaseIndex(
                                  caseItem.caseId
                                )}].forecasts[${forecastIndex}].dlom[${getDlomIndexByInstrumentId(
                                  forecast,
                                  instrument.instrumentId
                                )}].value`}
                                {...percentageFieldFormattingProps}
                                rules={{ ...minMaxValidator(0, 100) }}
                              />
                            )}
                          </td>
                        ));
                      })}
                  </tr>
                  {instrument.type ===
                    enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity) && (
                    <Fragment key={`ordinary-equity-row-${instrument.instrumentId}`}>
                      {instrument?.payoutLogic?.map((tranche, index) => (
                        <tr key={'instrument-row' + instrument.instrumentId + index}>
                          <Fragment key={tranche.id + 'tranche-row'}>
                            <td className="table-primary__cell--vertical-separator table-primary__cell--indentation">{`Tranche ${
                              index + 1
                            }`}</td>
                            {casesStructure.map((caseItem) => {
                              return caseItem.forecasts.map((forecast, forecastIndex) => (
                                <td
                                  key={forecast.forecastId + 'value'}
                                  className={classNames({
                                    'table-primary__cell--vertical-separator':
                                      caseItem.forecasts.length - 1 === forecastIndex,
                                  })}>
                                  <GridTextField
                                    name={`cases[${findSortedCaseIndex(
                                      caseItem.caseId
                                    )}].forecasts[${forecastIndex}].dlom[${getDlomIndexByTrancheId(
                                      forecast,
                                      tranche
                                    )}].value`}
                                    {...percentageFieldFormattingProps}
                                    rules={{ ...minMaxValidator(0, 100) }}
                                  />
                                </td>
                              ));
                            })}
                          </Fragment>
                        </tr>
                      ))}
                    </Fragment>
                  )}
                  {instrumentIndex < sortedValuedInstrument.length - 1 && (
                    <tr>
                      <td className="table-primary__cell--vertical-separator" />
                      {casesStructure.map((caseItem) => {
                        return caseItem.forecasts.map((forecast, index) => (
                          <Fragment key={forecast.forecastId + 'forecast-value-secondary'}>
                            <td
                              className={classNames({
                                'table-primary__cell--vertical-separator':
                                  index === caseItem.forecasts.length - 1,
                              })}
                            />
                          </Fragment>
                        ));
                      })}
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </form>
    </FormProvider>
  );
};

export default DLOMTable;
